import type { MutableRefObject } from 'react'

export const SCROLL_OFFSET = 10

const AUTO_SCROLL_TRIGGER_ZONE = 50

const AUTO_SCROLL_SPEED = 2
const MAX_AUTO_SCROLL_SPEED = 8

export const scrollButtonIntoView = (
  buttonElement: HTMLElement | null,
  scrollContainer: HTMLElement | null
): void => {
  if (!buttonElement || !scrollContainer) return

  const containerRect = scrollContainer.getBoundingClientRect()
  const buttonRect = buttonElement.getBoundingClientRect()

  const isCutOff = buttonRect.right > containerRect.right

  if (isCutOff) {
    const scrollAmount =
      buttonRect.right -
      containerRect.right +
      scrollContainer.scrollLeft +
      SCROLL_OFFSET

    scrollContainer.scrollTo({
      left: scrollAmount,
      behavior: 'smooth'
    })
  }
}

const getMaxScrollLeft = (scrollContainer: HTMLElement): number => {
  return scrollContainer.scrollWidth - scrollContainer.clientWidth
}

const cancelAnimationFrameIfActive = (
  animationFrameRef: MutableRefObject<number | null>
): void => {
  if (animationFrameRef.current !== null) {
    cancelAnimationFrame(animationFrameRef.current)
    animationFrameRef.current = null
  }
}

const calculateScrollSpeed = (distanceFromEdge: number): number => {
  const speedMultiplier =
    1 + (AUTO_SCROLL_TRIGGER_ZONE - distanceFromEdge) / AUTO_SCROLL_TRIGGER_ZONE
  return Math.min(AUTO_SCROLL_SPEED * speedMultiplier, MAX_AUTO_SCROLL_SPEED)
}

export const createPerformAutoScroll = (
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>,
  isMouseOverScrollAreaRef: MutableRefObject<boolean>,
  currentMouseXRef: MutableRefObject<number | null>,
  animationFrameRef: MutableRefObject<number | null>
): (() => void) => {
  const performScroll = () => {
    const scrollContainer = scrollContainerRef.current
    const isMouseOverScrollArea = isMouseOverScrollAreaRef.current
    const mouseX = currentMouseXRef.current

    if (!scrollContainer || !isMouseOverScrollArea || mouseX === null) {
      cancelAnimationFrameIfActive(animationFrameRef)
      return
    }

    const containerRect = scrollContainer.getBoundingClientRect()
    const relativeX = mouseX - containerRect.left
    const containerWidth = containerRect.width

    const isLeftZone = relativeX < AUTO_SCROLL_TRIGGER_ZONE
    const isRightZone = relativeX > containerWidth - AUTO_SCROLL_TRIGGER_ZONE

    if (isLeftZone && scrollContainer.scrollLeft > 0) {
      const scrollSpeed = calculateScrollSpeed(relativeX)
      scrollContainer.scrollLeft -= scrollSpeed
      animationFrameRef.current = requestAnimationFrame(performScroll)
    } else if (isRightZone) {
      const maxScrollLeft = getMaxScrollLeft(scrollContainer)
      if (scrollContainer.scrollLeft < maxScrollLeft) {
        const scrollSpeed = calculateScrollSpeed(containerWidth - relativeX)
        scrollContainer.scrollLeft += scrollSpeed
        animationFrameRef.current = requestAnimationFrame(performScroll)
      } else {
        cancelAnimationFrameIfActive(animationFrameRef)
      }
    } else {
      cancelAnimationFrameIfActive(animationFrameRef)
    }
  }

  return performScroll
}
